import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Loader2, Clock } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { clearOnboardingCache } from "../components/OnboardingGate";
import { AccountTypeStep, AgencyStep, ProfileStep, CalendarStep, ChannelsStep, FirstClientStep, PreferencesStep, ProgramStep } from "../components/onboarding/steps";
import { useT } from "../lib/i18n";

const SUMMARY = {
  account_type: (d, st) => (st.account_type === "agency" ? "Agency-account" : "Solo agent"),
  agency: (d) => `${d.members} teamlid/leden · ${d.pending_invites} uitnodiging(en) open`,
  profile: (d, st) => `${st.user?.name || ""}${st.user?.phone ? ` · ${st.user.phone}` : ""}`,
  calendar: (d) => (d.calendar ? "Playmaker-agenda actief, AI-herkenning aan" : "Nog niet ingesteld"),
  channels: (d) => `${d.email_connected ? `E-mail: ${d.email_addr}` : "E-mail niet verbonden"} · WhatsApp ${d.whatsapp_connected ? "actief" : "niet actief"}`,
  first_client: (d) => `${d.clients} dossier(s)`,
  preferences: (d) => (d.briefing?.enabled ? `Briefing ${d.briefing.frequency} via ${d.briefing.channel}` : "Briefing uit"),
  program: (d) => (d.program ? "12-weekprogramma aangemaakt" : "Nog geen programma"),
};

export default function OnboardingPage() {
  const t = useT();
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [params] = useSearchParams();
  const focusStep = params.get("step");
  const [state, setState] = useState(null);
  const [idx, setIdx] = useState(0);
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [finished, setFinished] = useState(false);

  const load = useCallback(async () => { const { data } = await api.get("/onboarding/state"); setState(data); return data; }, []);
  useEffect(() => {
    load().then((s) => {
      const keys = [...s.steps, ...(s.followups || [])].map((x) => x.key);
      if (focusStep && keys.includes(focusStep)) setIdx(keys.indexOf(focusStep));
      else if (s.next_step) setIdx(s.steps.findIndex((x) => x.key === s.next_step));
      else if (s.completed_at) setFinished(true);
      setData({ name: s.user?.name, phone: s.detected?.whatsapp_to || s.user?.phone, whatsapp_to: s.detected?.whatsapp_to || s.user?.phone });
    });
  }, [load, focusStep]);

  const steps = focusStep ? [...(state?.steps || []), ...(state?.followups || [])] : (state?.steps || []);
  const step = steps[idx];
  const total = state?.steps?.length || 0;
  const isAgency = state?.account_type === "agency";

  const submit = async (action) => {
    if (!step) return;
    if (action === "complete" && step.key === "account_type" && !data.account_type) { setErr(t("Kies solo of agency.")); return; }
    if (action === "complete" && step.key === "profile" && !(data.name || "").trim()) { setErr(t("Vul je naam in.")); return; }
    setBusy(true); setErr("");
    try {
      const payload = step.key === "account_type" ? { account_type: data.account_type, agency_name: data.agency_name }
        : step.key === "profile" ? { name: data.name, phone: data.phone, agency_name: data.agency_name }
        : step.key === "calendar" ? { provider: data.provider || "native" }
        : step.key === "channels" ? { whatsapp_to: data.phone }
        : step.key === "preferences" ? { briefing_frequency: data.briefing_frequency || "daily", channel: data.channel || "email", whatsapp_to: data.whatsapp_to, notify_trigger: data.notify_trigger || "full_time", match_report: data.match_report !== false }
        : step.key === "program" ? { title: data.title, vision: data.vision, goals: data.goals, tactics: data.tactics, targets: data.targets, scope: "agency" }
        : {};
      const { data: s } = await api.post(`/onboarding/steps/${step.key}`, { action, data: payload });
      setState(s);
      if (focusStep) {
        if (s.completed_at) await finish(s, true); else nav("/onboarding", { replace: true });
        return;
      }
      if (idx + 1 < s.steps.length) setIdx(idx + 1); else await finish(s);
    } catch (e) { setErr(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  const finish = async (s, quiet = false) => {
    try {
      const { data: done } = await api.post("/onboarding/complete");
      setState(done); clearOnboardingCache(); refreshUser?.();
      if (quiet) nav("/dashboard"); else setFinished(true);
    } catch (e) { setErr(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const body = useMemo(() => {
    if (!step || !state) return null;
    const d = state.detected;
    const reload = () => load();
    switch (step.key) {
      case "account_type": return <AccountTypeStep data={data} setData={setData} />;
      case "agency": return <AgencyStep detected={d} onDone={reload} />;
      case "profile": return <ProfileStep data={data} setData={setData} isAgency={isAgency} />;
      case "calendar": return <CalendarStep data={data} setData={setData} />;
      case "channels": return <ChannelsStep detected={d} onConnected={reload} data={data} setData={setData} />;
      case "first_client": return <FirstClientStep detected={d} onAdded={reload} />;
      case "preferences": return <PreferencesStep data={data} setData={setData} />;
      case "program": return <ProgramStep data={data} setData={setData} />;
      default: return null;
    }
  }, [step, state, data, isAgency, load]);

  if (!state) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-400">{t("Laden…")}</div>;

  if (finished) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6" data-testid="onboarding-done">
        <div className="w-full max-w-lg">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-5" style={{ background: "var(--pm-green)" }}><Check size={22} className="text-slate-900" /></div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{t("Je account staat klaar")}</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">{t("Dit is actief — en dit kun je later afmaken.")}</p>
          <div className="border-t border-slate-100">
            {state.steps.map((s) => (
              <div key={s.key} className="flex items-start gap-3 py-3 border-b border-slate-100" data-testid={`ob-summary-${s.key}`}>
                <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${s.status === "done" ? "bg-emerald-500" : "bg-orange-400"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">{t(s.title)}</div>
                  <div className="text-xs text-slate-500">{s.status === "done" ? t(SUMMARY[s.key]?.(state.detected, state)) : t("Overgeslagen — staat in je checklist op het dashboard")}</div>
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => nav("/dashboard")} className="mt-6 w-full h-12 rounded-full text-sm font-semibold text-slate-900 flex items-center justify-center gap-2" style={{ background: "var(--pm-green)" }} data-testid="onboarding-go-dashboard">{t("Naar Playmaker")} <ArrowRight size={16} /></button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="onboarding-page">
      <div className="h-1 bg-slate-100"><div className="h-full transition-[width] duration-300" style={{ width: `${((idx + 1) / total) * 100}%`, background: "var(--pm-green)" }} /></div>
      <div className="flex-1 flex items-start sm:items-center justify-center p-6">
        <div className="w-full max-w-lg">
          <div className="text-xs font-medium text-slate-400 tabular-nums mb-2" data-testid="onboarding-progress">{focusStep ? t("Vervolgstap") : t("Stap {a} van {b}", { a: idx + 1, b: total })}{!step.required && <span className="ml-2 text-slate-300"> · {t("optioneel")}</span>}</div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="onboarding-step-title">{t(step.title)}</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6 leading-relaxed" data-testid="onboarding-step-why">{t(step.why)}</p>
          <div data-testid={`onboarding-step-${step.key}`}>{body}</div>
          {err && <div className="text-xs text-red-600 mt-3" data-testid="onboarding-error">{err}</div>}
          <div className="flex items-center gap-2 mt-8">
            {idx > 0 && !focusStep && <button onClick={() => setIdx(idx - 1)} className="h-11 w-11 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center" data-testid="onboarding-back" aria-label={t("Terug")}><ArrowLeft size={16} /></button>}
            <button onClick={() => submit("complete")} disabled={busy} className="h-11 px-5 rounded-full text-sm font-semibold text-slate-900 flex items-center gap-2" style={{ background: "var(--pm-green)" }} data-testid="onboarding-next">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {idx + 1 === total ? t("Afronden") : t("Doorgaan")}
            </button>
            {!step.required && <button onClick={() => submit("skip")} disabled={busy} className="h-11 px-4 rounded-full text-sm font-medium text-slate-500 flex items-center gap-1.5" data-testid="onboarding-skip"><Clock size={14} /> {t("Later doen")}</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
